/**
 * Brüder Mannesmann product-datasheet PDF (jsPDF, A4, all units mm).
 *
 * Rendering logic only — generateDatasheetPdf(data) takes a plain data object built
 * from Liquid tags in snippets/product_datasheet.liquid and returns a jsPDF document.
 * Requires jspdf.min.js to be loaded first (window.jspdf).
 */

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 12.6;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;
const MM_PER_PT = 0.352778;

const COLOR = {
  black: [17, 20, 23],
  white: [255, 255, 255],
  gray: [107, 114, 128],
  grayLight: [154, 160, 166],
  divider: [229, 231, 235],
  tableBg: [246, 247, 249],
  priceBoxBg: [15, 20, 23],
};

const LAYOUT = {
  header: {
    logo: { x: MARGIN, w: 57, h: 14.9 },
    eyebrowY: 15.9,
    eyebrowSize: 10.5,
    eyebrowSpacing: 0.85,
    breadcrumbY: 22,
    breadcrumbSize: 7.5,
    ruleY: 27.3,
    ruleThickness: 0.8,
  },
  title: {
    y: 42.3,
    size: 20,
    lineHeightFactor: 1.15,
  },
  grid: {
    gap: 2,
    col1Width: 68,
    totalHeight: 86.5,
    topGapAfterTitle: 8.2,
  },
  features: {
    eyebrowSize: 10,
    eyebrowSpacing: 0.85,
    gapAboveRule: 2.95,
    gapBelowRule: 5.9,
    gutter: 11,
    rightColWidth: 66.2,
    bulletSize: 1.4,
    bulletBaselineOffset: 1.7,
    gapAfterBullet: 3,
    fontSize: 8,
    lineHeightFactor: 1.4,
    itemSpacing: 2.5,
  },
  table: {
    rowHeight: 7.3,
    padding: 6,
    labelSize: 8,
    valueSize: 8,
    valueLineHeightFactor: 1.3,
    labelValueGap: 4,
  },
  priceBox: {
    gapAfterTable: 15.2,
    padding: 6,
    eyebrowBaselineOffset: 3,
    priceBaselineOffset: 16,
    mwstBaselineOffset: 21.5,
    dividerOffset: 24.5,
    uvpBaselineOffset: 28.5,
    eyebrowSize: 10,
    eyebrowSpacing: 0.7,
    priceSize: 25,
    secondarySize: 8,
  },
  body: {
    gapAfterColumns: 6.2,
    bodySize: 8,
    lineHeight: 3.4,
    blockGap: 3,
    bottomMargin: 6,
    continuationTop: 40,
  },
  footer: {
    barTop: 286.5,
    barHeight: PAGE_HEIGHT - 286.5,
  },
  backgroundImage: {
    size: 90,
  },
};

/* ---------------------------------- helpers ---------------------------------- */

// Mirrors the theme's own fonts (all embedded as real TTFs by datasheet-fonts.js --
// jsPDF's built-in 'helvetica' can't even render this file's own glyphs, e.g. ¼):
//   family: 'body'    -> Roboto (settings.type_base_font_family), normal or bold per `bold`
//   family: 'heading' -> Roboto Condensed (settings.type_heading_font_family) -- the theme
//                        only defines this at weight 700, so it's always bold regardless of `bold`
function setFont(doc, family, bold, size, color) {
  if (family === 'heading') {
    doc.setFont('RobotoCondensed', 'bold');
  } else {
    doc.setFont('Roboto', bold ? 'bold' : 'normal');
  }
  doc.setFontSize(size);
  if (color) doc.setTextColor(...color);
}

function letterSpacedWidth(doc, text, spacing) {
  let width = 0;
  for (const char of text) width += doc.getTextWidth(char) + spacing;
  return text.length ? width - spacing : 0;
}

// Draws all-caps "eyebrow" labels with manual letter-spacing; x is the left edge,
// or the right edge when align is 'right'.
function drawLetterSpaced(doc, text, x, y, spacing, align = 'left') {
  const totalWidth = letterSpacedWidth(doc, text, spacing);
  const drawX = align === 'right' ? x - totalWidth : x;
  // setCharSpace uses the PDF's own character-spacing operator, so the text stays one
  // real string in the PDF (copy/paste and search work) instead of separate per-letter
  // glyphs that some tools re-join with spurious spaces between every character.
  doc.setCharSpace(spacing);
  doc.text(text, drawX, y);
  doc.setCharSpace(0);
  return totalWidth;
}

// Right-aligned breadcrumb: all but the last segment are muted/regular, the last is bold/dark.
function drawBreadcrumb(doc, segments, xRight, y, { size, mutedColor, activeColor, separatorColor }) {
  const separator = ' › ';
  setFont(doc, 'body', false, size);
  const sepWidth = doc.getTextWidth(separator);
  const widths = segments.map((segment, i) => {
    const isLast = i === segments.length - 1;
    setFont(doc, isLast ? 'heading' : 'body', isLast, size);
    return doc.getTextWidth(segment);
  });
  const totalWidth = widths.reduce((sum, w) => sum + w, 0) + sepWidth * (segments.length - 1);

  let cursor = xRight - totalWidth;
  segments.forEach((segment, i) => {
    const isLast = i === segments.length - 1;
    setFont(doc, isLast ? 'heading' : 'body', isLast, size, isLast ? activeColor : mutedColor);
    doc.text(segment, cursor, y);
    cursor += widths[i];
    if (!isLast) {
      setFont(doc, 'body', false, size, separatorColor);
      doc.text(separator, cursor, y);
      cursor += sepWidth;
    }
  });
}

// Splits mixed bold/regular runs into words tagged with their style, then greedily
// wraps them to maxWidth. Returns an array of lines, each an array of {text, bold}.
function wrapRuns(doc, runs, maxWidth, baseSize) {
  const words = [];
  runs.forEach((run) => {
    run.text.split(' ').forEach((word) => {
      if (word.length) words.push({ text: word, bold: !!run.bold });
    });
  });

  const lines = [];
  let current = [];
  let currentWidth = 0;

  words.forEach((word) => {
    setFont(doc, 'body', word.bold, baseSize);
    const wordWidth = doc.getTextWidth(word.text);
    const spaceWidth = current.length ? doc.getTextWidth(' ') : 0;
    if (current.length && currentWidth + spaceWidth + wordWidth > maxWidth) {
      lines.push(current);
      current = [word];
      currentWidth = wordWidth;
    } else {
      current.push(word);
      currentWidth += spaceWidth + wordWidth;
    }
  });
  if (current.length) lines.push(current);
  return lines;
}

function drawRunLines(doc, lines, x, y, size, lineHeight, color) {
  setFont(doc, 'body', false, size, color);
  lines.forEach((line, i) => {
    let cursor = x;
    line.forEach((word) => {
      setFont(doc, 'body', word.bold, size, color);
      doc.text(word.text, cursor, y + i * lineHeight);
      cursor += doc.getTextWidth(word.text) + doc.getTextWidth(' ');
    });
  });
}

// Same as CSS "object-fit: contain": scales an image as large as possible within a
// box without stretching or cropping it, then centers it. Returns box-relative offsets.
function fitContain(imageWidth, imageHeight, boxWidth, boxHeight) {
  const scale = Math.min(boxWidth / imageWidth, boxHeight / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  return { width, height, x: (boxWidth - width) / 2, y: (boxHeight - height) / 2 };
}

// Placeholder for an image slot that has no real asset yet (header/footer logo, and
// any product-image slot beyond what the product actually has).
function drawPlaceholderImage(doc, x, y, w, h, label) {
  doc.setDrawColor(206, 210, 214);
  doc.setLineWidth(0.25);
  doc.setFillColor(247, 248, 249);
  doc.roundedRect(x, y, w, h, 1.5, 1.5, 'FD');
  doc.line(x, y, x + w, y + h);
  doc.line(x + w, y, x, y + h);
  setFont(doc, 'body', false, 7, [150, 154, 158]);
  doc.text(label, x + w / 2, y + h / 2, { align: 'center', baseline: 'middle' });
}

function drawPlaceholderLogo(doc, x, y, w, h, { light = false } = {}) {
  const border = light ? [255, 255, 255] : [206, 210, 214];
  const text = light ? [255, 255, 255] : [150, 154, 158];
  doc.setDrawColor(...border);
  doc.setLineWidth(0.25);
  if (!light) {
    doc.setFillColor(247, 248, 249);
    doc.roundedRect(x, y, w, h, 1.2, 1.2, 'FD');
  } else {
    doc.roundedRect(x, y, w, h, 1.2, 1.2, 'D');
  }
  setFont(doc, 'heading', true, light ? 6 : 7, text);
  doc.text('LOGO', x + w / 2, y + h / 2, { align: 'center', baseline: 'middle' });
}

// Draws a loaded logo image scaled to fit inside (w, h) without stretching or cropping
// (same object-fit: contain treatment as product images); falls back to the placeholder
// box when no image was supplied.
function drawLogo(doc, image, x, y, w, h, options) {
  if (image && typeof image !== 'string') {
    const fit = fitContain(image.naturalWidth || image.width, image.naturalHeight || image.height, w, h);
    doc.addImage(image, 'PNG', x + fit.x, y + fit.y, fit.width, fit.height);
  } else {
    drawPlaceholderLogo(doc, x, y, w, h, options);
  }
}

/* ---------------------------------- sections ---------------------------------- */

function drawHeader(doc, data) {
  const h = LAYOUT.header;

  setFont(doc, 'body', false, h.eyebrowSize, data.highlightColor);
  drawLetterSpaced(doc, data.eyebrow.toUpperCase(), PAGE_WIDTH - MARGIN, h.eyebrowY, h.eyebrowSpacing, 'right');

  drawBreadcrumb(doc, data.breadcrumbs, PAGE_WIDTH - MARGIN, h.breadcrumbY, {
    size: h.breadcrumbSize,
    mutedColor: COLOR.gray,
    activeColor: COLOR.black,
    separatorColor: data.highlightColor,
  });

  // Center the logo against the visual span of the eyebrow+breadcrumb text block
  // (cap-height above the eyebrow baseline, to a hair below the breadcrumb baseline).
  const textTop = h.eyebrowY - h.eyebrowSize * MM_PER_PT * 0.72;
  const textBottom = h.breadcrumbY + h.breadcrumbSize * MM_PER_PT * 0.22;
  const logoY = (textTop + textBottom) / 2 - h.logo.h / 2;
  drawLogo(doc, data.headerLogo, h.logo.x, logoY, h.logo.w, h.logo.h);

  doc.setDrawColor(...data.highlightColor);
  doc.setLineWidth(h.ruleThickness);
  doc.line(MARGIN, h.ruleY, PAGE_WIDTH - MARGIN, h.ruleY);
}

// Returns the y position immediately below the (possibly multi-line) title.
function drawTitle(doc, data) {
  const t = LAYOUT.title;
  setFont(doc, 'heading', true, t.size, COLOR.black);
  const lineHeight = t.size * MM_PER_PT * t.lineHeightFactor;
  const lines = doc.splitTextToSize(data.title, CONTENT_WIDTH);
  lines.forEach((line, i) => doc.text(line, MARGIN, t.y + i * lineHeight));
  return t.y + (lines.length - 1) * lineHeight;
}

// Draws a single grid cell: a real image (added directly, same as the old snippet's
// proven addImage(productImg, ...) calls) when `image` is an HTMLImageElement,
// otherwise a placeholder box (using `image` itself as the label when it's a string).
// Deliberately does NOT go through toCleanDataUrl()/canvas here: canvas-based
// re-encoding taints on cross-origin images unless crossOrigin is set *and* the CDN
// grants it, which isn't worth the risk for a path that already works.
function drawImageCell(doc, image, x, y, w, h) {
  if (image && typeof image !== 'string') {
    const fit = fitContain(image.naturalWidth || image.width, image.naturalHeight || image.height, w, h);
    doc.addImage(image, 'JPEG', x + fit.x, y + fit.y, fit.width, fit.height);
  } else {
    drawPlaceholderImage(doc, x, y, w, h, image || 'Product image');
  }
}

// 1 large hero image (left) + up to 4 more filling the right side, in up to 2 rows of up
// to 2 columns. A row/cell with only one image spans the full available width, and a
// hero with nothing left to pair with expands to the full grid width — so this adapts
// automatically to however many images the product actually has (1–5).
function drawImageGrid(doc, data, gridTop) {
  const g = LAYOUT.grid;
  const images = (data.images || []).slice(0, 5);

  if (images.length === 0) {
    drawPlaceholderImage(doc, MARGIN, gridTop, CONTENT_WIDTH, g.totalHeight, 'Product image');
    return gridTop + g.totalHeight;
  }

  const [hero, ...rest] = images;
  const heroWidth = rest.length === 0 ? CONTENT_WIDTH : g.col1Width;
  drawImageCell(doc, hero, MARGIN, gridTop, heroWidth, g.totalHeight);
  if (rest.length === 0) return gridTop + g.totalHeight;

  const rightColX = MARGIN + g.col1Width + g.gap;
  const rightColWidth = CONTENT_WIDTH - g.col1Width - g.gap;
  const rows = rest.length > 2 ? [rest.slice(0, 2), rest.slice(2, 4)] : [rest];
  const rowHeight = rows.length === 1 ? g.totalHeight : (g.totalHeight - g.gap) / 2;
  // A single cell only spans the full row width when it's the *only* image on the right
  // side (2 images total). Once there's a second row (4 or 5 images total), every cell
  // is half-width — including a lone 4th image — so the 4- and 5-image layouts share
  // identical grid geometry; the 4-image case just leaves one cell blank.
  const useHalfWidthCells = rest.length !== 1;

  rows.forEach((row, rowIndex) => {
    const rowY = gridTop + rowIndex * (rowHeight + g.gap);
    const cellWidth = useHalfWidthCells ? (rightColWidth - g.gap) / 2 : rightColWidth;
    row.forEach((image, colIndex) => {
      const cellX = rightColX + colIndex * (cellWidth + g.gap);
      drawImageCell(doc, image, cellX, rowY, cellWidth, rowHeight);
    });
  });

  return gridTop + g.totalHeight;
}

// Bulleted feature list; each item is a run array (from extractFeatures's <strong>
// parsing) wrapping onto multiple lines as needed. Returns the y position immediately
// below the last bullet.
function drawFeatures(doc, data, top, columnWidth) {
  const f = LAYOUT.features;
  setFont(doc, 'body', false, f.eyebrowSize, data.highlightColor);
  drawLetterSpaced(doc, data.featuresHeading, MARGIN, top, f.eyebrowSpacing);

  const ruleY = top + f.gapAboveRule;
  doc.setDrawColor(...COLOR.divider);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, ruleY, MARGIN + columnWidth, ruleY);

  const textX = MARGIN + f.bulletSize + f.gapAfterBullet;
  const textMaxWidth = columnWidth - (f.bulletSize + f.gapAfterBullet);
  const lineHeight = f.fontSize * MM_PER_PT * f.lineHeightFactor;

  // The bullet square's top edge sits above the text baseline by this much — account
  // for it so the visual gap below the rule lands exactly at gapBelowRule, not short of it.
  const bulletTopOffset = f.bulletBaselineOffset;
  let cursorY = ruleY + f.gapBelowRule + bulletTopOffset;
  (data.features || []).forEach((runs) => {
    const lines = wrapRuns(doc, runs, textMaxWidth, f.fontSize);

    doc.setFillColor(...data.highlightColor);
    doc.rect(MARGIN, cursorY - bulletTopOffset, f.bulletSize, f.bulletSize, 'F');
    drawRunLines(doc, lines, textX, cursorY, f.fontSize, lineHeight, COLOR.black);

    cursorY += lines.length * lineHeight + f.itemSpacing;
  });

  return cursorY - f.itemSpacing;
}

// Light-gray key/value table (SKU, EAN, brand, weight, ...). A value that's too long to
// fit next to its label (e.g. a long brand name) wraps onto extra lines instead of
// overlapping the label; the row (and table) height grows to fit.
function drawSpecTable(doc, data, x, y, width) {
  const t = LAYOUT.table;
  const rows = data.specs || [];

  const lineHeight = t.valueSize * MM_PER_PT * t.valueLineHeightFactor;
  const verticalPadding = t.rowHeight - lineHeight;

  const wrappedRows = rows.map((row) => {
    setFont(doc, 'body', false, t.labelSize, COLOR.gray);
    const labelWidth = doc.getTextWidth(row.label);
    const valueMaxWidth = width - t.padding * 2 - labelWidth - t.labelValueGap;

    setFont(doc, 'body', true, t.valueSize, COLOR.black);
    const value = row.value == null ? '' : String(row.value);
    const lines = doc.splitTextToSize(value, valueMaxWidth);
    return { label: row.label, lines, rowHeight: lines.length * lineHeight + verticalPadding };
  });

  const height = wrappedRows.reduce((sum, row) => sum + row.rowHeight, 0);

  doc.setFillColor(...COLOR.tableBg);
  doc.rect(x, y, width, height, 'F');

  let rowTop = y;
  wrappedRows.forEach((row, i) => {
    const { rowHeight, lines } = row;

    if (i > 0) {
      doc.setDrawColor(...COLOR.divider);
      doc.setLineWidth(0.2);
      doc.line(x + t.padding, rowTop, x + width - t.padding, rowTop);
    }

    setFont(doc, 'body', false, t.labelSize, COLOR.gray);
    doc.text(row.label, x + t.padding, rowTop + rowHeight / 2 + t.valueSize * MM_PER_PT * 0.32);

    setFont(doc, 'body', true, t.valueSize, COLOR.black);
    const valueBlockTop = rowTop + (rowHeight - lines.length * lineHeight) / 2;
    lines.forEach((line, li) => {
      const baseline = valueBlockTop + li * lineHeight + lineHeight / 2 + t.valueSize * MM_PER_PT * 0.32;
      doc.text(line, x + width - t.padding, baseline, { align: 'right' });
    });

    rowTop += rowHeight;
  });

  return y + height;
}

// Dark price panel with sale price + optional strike-through RRP. Height is derived
// from whichever line is last (so top/bottom padding stay equal whether or not
// there's a compare-at price), not fixed.
function drawPriceBox(doc, data, x, y, width) {
  const p = LAYOUT.priceBox;
  const hasCompareAtPrice = !!data.compareAtPrice;

  const lastBaselineOffset = hasCompareAtPrice ? p.uvpBaselineOffset : p.mwstBaselineOffset;
  const lastSize = hasCompareAtPrice ? p.secondarySize + 1 : p.secondarySize;
  const lastLineDescender = lastSize * MM_PER_PT * 0.22;
  const height = p.padding + lastBaselineOffset + lastLineDescender + p.padding;

  doc.setFillColor(...COLOR.priceBoxBg);
  doc.rect(x, y, width, height, 'F');

  const contentX = x + p.padding;
  const contentTop = y + p.padding;

  setFont(doc, 'body', false, p.eyebrowSize, data.highlightColor);
  drawLetterSpaced(doc, data.priceHeading, contentX, contentTop + p.eyebrowBaselineOffset, p.eyebrowSpacing);

  setFont(doc, 'heading', true, p.priceSize, COLOR.white);
  doc.text(data.price, contentX, contentTop + p.priceBaselineOffset);

  setFont(doc, 'body', false, p.secondarySize, COLOR.grayLight);
  doc.text(data.vatIncludedLabel, contentX, contentTop + p.mwstBaselineOffset);

  if (hasCompareAtPrice) {
    doc.setDrawColor(70, 75, 79);
    doc.setLineWidth(0.2);
    doc.line(contentX, contentTop + p.dividerOffset, x + width - p.padding, contentTop + p.dividerOffset);

    setFont(doc, 'body', false, p.secondarySize + 1, COLOR.grayLight);
    doc.text(`${data.rrpLabel} ${data.compareAtPrice}`, contentX, contentTop + p.uvpBaselineOffset);
  }

  return y + height;
}

// Plain paragraph blocks (full-width body copy below both columns). This is the only
// section expected to overflow a page (feature bullets/spec table/price box are assumed
// to always fit above it on page 1), so it's also the only section that paginates: once a
// line would cross the footer bar, the current page's footer is drawn, a new page starts
// with the same header repeated, and body copy continues from there -- mid-paragraph if
// that's where it broke. Draws the footer itself (including on the last page) so every
// page gets exactly one, however many pages that ends up being.
function drawBodyCopy(doc, data, top) {
  const b = LAYOUT.body;
  const pageBottom = LAYOUT.footer.barTop - b.bottomMargin;
  let cursorY = top;

  function ensureRoomFor(neededHeight) {
    if (cursorY + neededHeight <= pageBottom) return;
    drawFooter(doc, data);
    doc.addPage();
    drawHeader(doc, data);
    // drawHeader leaves the doc's font wherever its own drawing (e.g. the breadcrumb's
    // bold/heading active segment) last set it -- restore body copy's font before the
    // caller's next doc.text() call.
    setFont(doc, 'body', false, b.bodySize, COLOR.black);
    cursorY = b.continuationTop;
  }

  (data.paragraphs || []).forEach((paragraph) => {
    setFont(doc, 'body', false, b.bodySize, COLOR.black);
    const lines = doc.splitTextToSize(paragraph, CONTENT_WIDTH);
    lines.forEach((line) => {
      ensureRoomFor(b.lineHeight);
      doc.text(line, MARGIN, cursorY);
      cursorY += b.lineHeight;
    });
    cursorY += b.blockGap;
  });

  drawFooter(doc, data);
  return cursorY;
}

function drawFooter(doc, data) {
  const f = LAYOUT.footer;
  doc.setFillColor(...data.highlightColor);
  doc.rect(0, f.barTop, PAGE_WIDTH, f.barHeight, 'F');
  drawLogo(doc, data.footerLogo, MARGIN, f.barTop + (f.barHeight - 6) / 2, 24, 6, { light: true });
}

// Re-encodes an image through an offscreen canvas before handing it to jsPDF. jsPDF's
// bundled JPEG parser reads the source file's raw bytes itself to pull out width/height,
// and can misread real-world files (e.g. progressive JPEGs) — confirmed during testing
// with a perfectly square, undamaged source image that jsPDF nonetheless misjudged,
// causing a subtle skew. Re-encoding via canvas guarantees jsPDF only ever sees a
// simple, standard JPEG. Already-clean data URIs are passed through as-is.
function toCleanDataUrl(image) {
  if (typeof image === 'string') return image;
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  canvas.getContext('2d').drawImage(image, 0, 0);
  return canvas.toDataURL('image/jpeg', 0.92);
}

// Decorative square, pinned to the page's bottom-right corner, drawn before everything
// else so it sits at the lowest z-order — the price box, footer, etc. paint over it where
// they overlap. data.backgroundImage should be a loaded HTMLImageElement; falls back to
// a placeholder box when absent (no final asset from the client yet).
function drawBackgroundImage(doc, data) {
  const size = LAYOUT.backgroundImage.size;
  const x = PAGE_WIDTH - size;
  const y = PAGE_HEIGHT - size;
  if (data.backgroundImage) {
    doc.addImage(toCleanDataUrl(data.backgroundImage), 'JPEG', x, y, size, size);
  } else {
    drawPlaceholderImage(doc, x, y, size, size, 'Background image');
  }
}

/* ---------------------------------- entry point ---------------------------------- */

function generateDatasheetPdf(data) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', putOnlyUsedFonts: true, compress: true });
  if (data.fileName) doc.setProperties({ title: data.fileName });

  drawBackgroundImage(doc, data);
  drawHeader(doc, data);
  const titleBottom = drawTitle(doc, data);

  const g = LAYOUT.grid;
  const gridTop = titleBottom + g.topGapAfterTitle;
  const gridBottom = drawImageGrid(doc, data, gridTop);

  const featuresTop = gridBottom + (LAYOUT.features.eyebrowSize * MM_PER_PT) + 3;
  const columnsRight = MARGIN + CONTENT_WIDTH;
  const rightColX = columnsRight - LAYOUT.features.rightColWidth;
  const leftColWidth = rightColX - LAYOUT.features.gutter - MARGIN;

  const featuresBottom = drawFeatures(doc, data, featuresTop, leftColWidth);

  const tableBottom = drawSpecTable(doc, data, rightColX, featuresTop, LAYOUT.features.rightColWidth);
  const priceBoxTop = tableBottom + LAYOUT.priceBox.gapAfterTable;
  const priceBoxBottom = drawPriceBox(doc, data, rightColX, priceBoxTop, LAYOUT.features.rightColWidth);

  const bodyTop = Math.max(featuresBottom, priceBoxBottom) + LAYOUT.body.gapAfterColumns;
  drawBodyCopy(doc, data, bodyTop); // draws the footer itself, once per page (see comment there)

  return doc;
}
